import { Outlet, createRootRouteWithContext, createRoute, lazyRouteComponent, type RouteComponent } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ADMIN_TAB, PRIORITY, TICKET_SORT, TICKET_STATUS, TICKET_TAB } from '@helpdesk/shared';
import { meQuery } from '../api/queries.ts';
import { AppShell } from '../components/layout.tsx';
import { OutboxAgent } from '../components/outbox-agent.tsx';
import { ForcedPasswordChange } from '../components/password.tsx';
import { NotFoundNote, PublicNotFoundNote, PublicRouteErrorNote } from '../components/route-fallback.tsx';
import { ErrorNote, Spinner } from '../components/ui.tsx';
import { ConfirmPage } from '../pages/confirm.tsx';
import { HelpPage, LoggedOutPage, PrivacyPage } from '../pages/info.tsx';
import { HomePage } from '../pages/home.tsx';
import { LoginPage } from '../pages/login.tsx';
import { MachinePage } from '../pages/machine.tsx';
import { MachineEditPage, MachinesPage } from '../pages/machines.tsx';
import { MachinePublicPage, MachineQuickReportPage } from '../pages/machine-public.tsx';
import { ProfilePage } from '../pages/profile.tsx';
import { ReportPage } from '../pages/report.tsx';
import { ScanPage } from '../pages/scan.tsx';
import { TabletSetupPage } from '../pages/tablet.tsx';
import { TicketPage } from '../pages/ticket.tsx';
import { TicketsPage } from '../pages/tickets.tsx';

type RouterContext = { queryClient: QueryClient };

/*
 * Selten genutzte Seiten werden erst beim Aufruf geladen – die App startet
 * auf dem Handy schneller. Der Service Worker hält sie trotzdem vor.
 */
const AdminPage = lazyRouteComponent(() => import('../pages/admin.tsx'), 'AdminPage');
const ContactsPage = lazyRouteComponent(() => import('../pages/contacts.tsx'), 'ContactsPage');
const KioskPage = lazyRouteComponent(() => import('../pages/kiosk.tsx'), 'KioskPage');
const LabelsPage = lazyRouteComponent(() => import('../pages/labels.tsx'), 'LabelsPage');
const MachineImportPage = lazyRouteComponent(() => import('../pages/machine-import.tsx'), 'MachineImportPage');
const ReportsPage = lazyRouteComponent(() => import('../pages/reports.tsx'), 'ReportsPage');
const TeamsPage = lazyRouteComponent(() => import('../pages/teams.tsx'), 'TeamsPage');

/** Ein altes Lesezeichen oder ein gekürzter Link darf keine Seite kosten: Was das Schema nicht kennt, fällt auf die Vorgabe. */
const tolerant = <T extends z.ZodType>(schema: T) => schema.optional().catch(undefined);

/** Aus `?q=1047` in einem eingefügten Link macht der Router eine Zahl. */
const searchText = tolerant(z.coerce.string());

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <div className="flex min-h-[100dvh] flex-col">
      <div className="flex-1">
        <Outlet />
      </div>
      <OutboxAgent />
    </div>
  ),
  errorComponent: PublicRouteErrorNote,
  notFoundComponent: PublicNotFoundNote,
});

/**
 * Angemeldeter Bereich: die Anmeldung wird EINMAL hier geprüft. Wer nicht
 * angemeldet ist, sieht die Anmeldeseite – nicht eine Sammlung von Fehlern.
 */
function AppLayout() {
  const me = useQuery(meQuery);
  if (me.isPending) {
    return (
      <div className="mx-auto max-w-[1240px] px-6">
        <Spinner label="Anmeldung wird geprüft …" />
      </div>
    );
  }
  if (me.isError) {
    return (
      <div className="mx-auto max-w-[640px] space-y-4 px-6 py-10">
        <ErrorNote error={me.error} />
        <button type="button" className="ad-button-outline" onClick={() => void me.refetch()}>
          Erneut versuchen
        </button>
      </div>
    );
  }
  if (!me.data) return <LoginPage />;
  if (me.data.user.mustChangePassword) return <ForcedPasswordChange user={me.data.user} />;
  return (
    <AppShell user={me.data.user}>
      <Outlet />
    </AppShell>
  );
}

/*
 * Seiten darunter zeigen Fehler über `defaultErrorComponent` (`main.tsx`) in der `AppShell`. Scheitert
 * `AppLayout` selbst, fehlt dieser Rahmen. `notFoundComponent` muss hier stehen, sonst sucht der Router
 * bis zur Wurzel weiter, und `/tickets/IH-1/xyz` fiele aus der `AppShell`.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppLayout,
  errorComponent: PublicRouteErrorNote,
  notFoundComponent: NotFoundNote,
});

const page = <TPath extends string>(path: TPath, component: RouteComponent) =>
  createRoute({ getParentRoute: () => appRoute, path, component });

const ticketsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets',
  validateSearch: z.object({
    tab: tolerant(z.enum(TICKET_TAB)),
    siteId: tolerant(z.uuid()),
    priority: tolerant(z.enum(PRIORITY)),
    status: tolerant(z.enum(TICKET_STATUS)),
    q: searchText,
    machineId: tolerant(z.uuid()),
    teamId: tolerant(z.uuid()),
    assignee: tolerant(z.union([z.uuid(), z.literal('me'), z.literal('none')])),
    sort: tolerant(z.enum(TICKET_SORT)),
    page: tolerant(z.coerce.number().int().min(1)),
    ansicht: tolerant(z.enum(['liste', 'karten'])),
  }),
  component: TicketsPage,
});

const ticketRoute = createRoute({ getParentRoute: () => appRoute, path: '/tickets/$number', component: TicketPage });

const reportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/melden',
  validateSearch: z.object({ machineId: tolerant(z.uuid()), withoutMachine: tolerant(z.boolean()), via: tolerant(z.literal('qr')) }),
  component: ReportPage,
});

const machinesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/maschinen',
  validateSearch: z.object({ q: searchText, siteId: tolerant(z.uuid()), areaId: tolerant(z.uuid()), stillgelegt: tolerant(z.boolean()) }),
  component: MachinesPage,
});

const machineRoute = createRoute({ getParentRoute: () => appRoute, path: '/maschinen/$id', component: MachinePage });

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/admin',
  validateSearch: z.object({ tab: tolerant(z.enum(ADMIN_TAB)) }),
  component: AdminPage,
});

const publicRoute = <TPath extends string>(path: TPath, component: RouteComponent) =>
  createRoute({ getParentRoute: () => rootRoute, path, component, errorComponent: PublicRouteErrorNote });

export const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    page('/', HomePage),
    reportRoute,
    page('/scan', ScanPage),
    ticketsRoute,
    ticketRoute,
    machinesRoute,
    page('/maschinen/neu', MachineEditPage),
    page('/maschinen/etiketten', LabelsPage),
    page('/maschinen/import', MachineImportPage),
    machineRoute,
    page('/kontakte', ContactsPage),
    page('/teams', TeamsPage),
    page('/auswertung', ReportsPage),
    page('/profil', ProfilePage),
    adminRoute,
  ]),
  publicRoute('/m/$code', MachinePublicPage),
  publicRoute('/m/$code/melden', MachineQuickReportPage),
  publicRoute('/kiosk/$token', KioskPage),
  publicRoute('/antwort/$token', ConfirmPage),
  publicRoute('/tablet/$token', TabletSetupPage),
  publicRoute('/abgemeldet', LoggedOutPage),
  publicRoute('/hilfe', HelpPage),
  publicRoute('/datenschutz', PrivacyPage),
]);
